// src/App.js - Route split: REACT_APP_MODE controls website vs SaaS app routing
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated } from './utils/tokenManager';

// Marketing Website (always imported — CRA webpack requires static imports)
// Only rendered in website mode via routing logic below
import { MarketingLayout } from './components/Marketing';
import {
  LandingPage,
  FeaturesPage,
  PricingPage,
  AboutPage,
  ContactPage,
  WaitlistPage,
  PrivacyPage as MarketingPrivacyPage,
  TermsPage,
  BlogPage,
  BlogPostPage
} from './pages/marketing';

// REACT_APP_MODE: "website" = marketing site (Vercel), anything else = SaaS app (local dev / app deployment)
const isWebsiteMode = process.env.REACT_APP_MODE === 'website';

// Lazy-load app components to prevent them from initializing on marketing pages
// (prevents MetaAdsService network errors when backend isn't running)
const AppShell = React.lazy(() => import('./components/Layout').then(m => ({ default: m.AppShell })));
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const CreateCampaign = React.lazy(() => import('./components/Campaigns/CreateCampaign'));
const AICampaignOptimization = React.lazy(() => import('./components/Campaigns/AICampaignOptimization'));
const CampaignsList = React.lazy(() => import('./components/Campaigns/CampaignsList'));
const DeleteData = React.lazy(() => import('./components/Compliance/DeleteData'));
const Privacy = React.lazy(() => import('./components/Compliance/Privacy'));
const MetaOAuthCallback = React.lazy(() => import('./components/Auth/MetaOAuthCallback'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const EmailVerification = React.lazy(() => import('./pages/auth/EmailVerification'));
const BusinessProfileSetup = React.lazy(() => import('./pages/onboarding/BusinessProfileSetup'));
const OnboardingComplete = React.lazy(() => import('./pages/onboarding/OnboardingComplete'));
const FirstScan = React.lazy(() => import('./pages/FirstScan'));
const ConnectAds = React.lazy(() => import('./pages/ConnectAds'));
const ConnectAccounts = React.lazy(() => import('./pages/ConnectAccounts'));
const BusinessProfileEdit = React.lazy(() => import('./pages/settings/BusinessProfileEdit'));
const ConversionReview = React.lazy(() => import('./pages/settings/ConversionReview'));
const AccountPreferences = React.lazy(() => import('./components/Settings/AccountPreferences'));
const GeneralSettings = React.lazy(() => import('./components/Settings/GeneralSettings'));
const StrategyCoachOverview = React.lazy(() => import('./components/StrategyCoach/StrategyCoachOverview'));
const InsightsOverview = React.lazy(() => import('./components/Insights').then(m => ({ default: m.InsightsOverview })));
const InsightsFeed = React.lazy(() => import('./components/Insights/InsightsFeed'));
// Development only: a test harness for execution plans. Not in production builds, where
// the path falls through to the catch-all redirect.
const ExecutionPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/ExecutionPreview')) : null;
const BrandHub = React.lazy(() => import('./components/Assets/BrandHub'));
const MetricsPage = React.lazy(() => import('./components/Metrics/MetricsPage'));
const SelectGoogleAccount = React.lazy(() => import('./pages/SelectGoogleAccount'));
const EmailAction = React.lazy(() => import('./pages/EmailAction'));
const PaidCallsSpend = React.lazy(() => import('./pages/admin/PaidCallsSpend'));

// SaaS app URL for redirecting login/register from the marketing site
const SAAS_APP_URL = process.env.REACT_APP_SAAS_URL || 'https://app.adgrowai.com';


// Placeholder components for missing features
const ComingSoonPage = ({ feature }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-8">
      <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
        <span className="text-white text-2xl font-bold">🚧</span>
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-4">{feature}</h1>
      <p className="text-gray-600 mb-6">This feature is coming soon! We're working hard to bring you the best experience.</p>
      <button
        onClick={() => window.history.back()}
        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
      >
        Go Back
      </button>
    </div>
  </div>
);

// Helper: redirects to external SaaS app URL (for website mode login/register links)
const ExternalRedirect = ({ to }) => {
  React.useEffect(() => { window.location.href = to; }, [to]);
  return null;
};

// Helper: smart redirect for app mode root — dashboard if logged in, login if not
const AppRootRedirect = () => {
  return <Navigate to={isAuthenticated() ? '/dashboard' : '/login'} replace />;
};

// Helper: redirect marketing paths to app root in app mode
const MarketingToAppRedirect = () => <AppRootRedirect />;

// DEACTIVATED: /view-stats (old Campaign Analytics page) now lives on /metrics as
// the Keyword Overview card — see KeywordOverviewCard.jsx and
// src/Archives/campaign-analytics-v2.js/.css for the pre-move page. All in-app
// links were repointed straight at /metrics, so this redirect (and the route
// below that used it) is disabled rather than deleted, in case /view-stats needs
// to be reactivated later. To bring it back: uncomment both this and the route,
// and re-add `useLocation` to the react-router-dom import above.
// const ViewStatsRedirect = () => {
//   const location = useLocation();
//   return <Navigate to={`/metrics${location.search}`} replace />;
// };

function App() {
  React.useEffect(() => {
    document.title = isWebsiteMode
      ? 'Adgrow AI - AI-Powered Marketing Automation'
      : 'Adgrow AI';
  }, []);

  return (
    <Router>
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050810', color: '#E8ECF6' }}>Loading...</div>}>
      <Routes>

        {isWebsiteMode ? (
          <>
            {/* ============================================
                WEBSITE MODE: Marketing site (Vercel)
                ============================================ */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/website" element={<Navigate to="/" replace />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/privacy-marketing" element={<MarketingPrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Route>

            {/* Login/Register redirect to SaaS app */}
            <Route path="/login" element={<ExternalRedirect to={`${SAAS_APP_URL}/login`} />} />
            <Route path="/register" element={<ExternalRedirect to={`${SAAS_APP_URL}/register`} />} />

            {/* Compliance pages still needed on marketing domain */}
            <Route path="/delete-data" element={<DeleteData />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/data-deletion" element={<DeleteData />} />
            <Route path="/privacy-policy" element={<Privacy />} />

            {/* Catch-all: send unknown routes to homepage */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            {/* ============================================
                APP MODE: SaaS application (local dev / app deployment)
                ============================================ */}

            {/* Root redirects to dashboard or login based on auth state */}
            <Route path="/" element={<AppRootRedirect />} />

            {/* Marketing routes redirect to app */}
            <Route path="/website" element={<MarketingToAppRedirect />} />
            <Route path="/features" element={<MarketingToAppRedirect />} />
            <Route path="/pricing" element={<MarketingToAppRedirect />} />
            <Route path="/about" element={<MarketingToAppRedirect />} />
            <Route path="/contact" element={<MarketingToAppRedirect />} />
            <Route path="/waitlist" element={<MarketingToAppRedirect />} />
            <Route path="/blog" element={<MarketingToAppRedirect />} />
            <Route path="/privacy-marketing" element={<MarketingToAppRedirect />} />
            <Route path="/terms" element={<MarketingToAppRedirect />} />

            {/* Authentication Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* The reset email links to /reset-password?token=…; the :token form is for older links */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email" element={<EmailVerification />} />

            {/* Onboarding Routes */}
            <Route path="/onboarding/business-profile" element={<BusinessProfileSetup />} />
            <Route path="/onboarding/complete" element={<OnboardingComplete />} />

            {/* OAuth Callback Routes. Google returns to the backend's GET /api/google-ads/auth/callback,
                which redirects to the app; there is no frontend Google callback page. */}
            <Route path="/auth/meta/callback" element={<MetaOAuthCallback />} />
            <Route path="/auth/facebook/callback" element={<MetaOAuthCallback />} />

            {/* Google Ads Account Selection (post-OAuth + switch account) */}
            <Route path="/select-google-account" element={<SelectGoogleAccount />} />

            {/* Email Action Confirmation (public — no auth required, token is the credential) */}
            <Route path="/email-action" element={<EmailAction />} />

            {/* Meta Compliance Pages (public) */}
            <Route path="/delete-data" element={<DeleteData />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/data-deletion" element={<DeleteData />} />
            <Route path="/privacy-policy" element={<Privacy />} />

            {/* Authenticated Routes (With AppShell) */}
            <Route element={<AppShell />}>
              {/* Dashboard / Overview */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/first-scan" element={<FirstScan />} />
              <Route path="/connect-ads" element={<ConnectAds />} />
              <Route path="/connect-accounts" element={<ConnectAccounts />} />

              {/* Campaigns */}
              <Route path="/create-campaign" element={<CreateCampaign />} />
              {/* <Route path="/view-stats" element={<ViewStatsRedirect />} /> — deactivated, see comment above */}
              <Route path="/ai-optimization" element={<AICampaignOptimization />} />
              <Route path="/campaigns" element={<CampaignsList />} />

              {/* Insights */}
              <Route path="/insights" element={<InsightsFeed />} />
              <Route path="/optimization-insights" element={<InsightsOverview />} />
              <Route path="/ai-insights" element={<InsightsOverview />} />

              {/* Asset Library */}
              <Route path="/assets" element={<BrandHub />} />

              {/* Metrics */}
              <Route path="/metrics" element={<MetricsPage />} />

              {/* Strategy Coach */}
              <Route path="/strategy-coach" element={<Navigate to="/strategy-coach/overview" replace />} />
              <Route path="/strategy-coach/overview" element={<StrategyCoachOverview />} />

              {/* Settings */}
              <Route path="/settings" element={<GeneralSettings />} />
              <Route path="/settings/business-profile" element={<BusinessProfileEdit />} />
              <Route path="/settings/conversions" element={<ConversionReview />} />
              <Route path="/settings/:section" element={<GeneralSettings />} />
              <Route path="/business-profile" element={<BusinessProfileEdit />} />
              <Route path="/billing" element={<ComingSoonPage feature="Billing" />} />
              <Route path="/help" element={<ComingSoonPage feature="Help & Support" />} />

              {/* Admin: paid-call spend (the backend allows admins only) */}
              <Route path="/admin/spend" element={<PaidCallsSpend />} />

              {/* Execution Preview (development only) */}
              {ExecutionPreview && <Route path="/execution-preview" element={<ExecutionPreview />} />}

              {/* Account Preferences */}
              <Route path="/account" element={<AccountPreferences />} />
              <Route path="/account/:section" element={<AccountPreferences />} />
              <Route path="/accounts" element={<Navigate to="/account" replace />} />
              <Route path="/accounts/connect" element={<ComingSoonPage feature="Connect Accounts" />} />

              {/* Platform-specific (Future) */}
              <Route path="/youtube-campaigns" element={<ComingSoonPage feature="YouTube Campaigns" />} />
              <Route path="/meta-campaigns" element={<ComingSoonPage feature="Meta Campaigns" />} />

              {/* Other Future Features */}
              <Route path="/crm-integration" element={<ComingSoonPage feature="CRM Integration" />} />
              <Route path="/lead-management" element={<ComingSoonPage feature="Lead Management" />} />
              <Route path="/payments" element={<ComingSoonPage feature="Payments" />} />
              <Route path="/compliance" element={<ComingSoonPage feature="Compliance Center" />} />
              <Route path="/data-export" element={<ComingSoonPage feature="Data Export" />} />
              <Route path="/ai-automation" element={<ComingSoonPage feature="AI Automation Rules" />} />
              <Route path="/ai-reporting" element={<ComingSoonPage feature="AI-Powered Reports" />} />

              {/* Catch all route within authenticated area */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </>
        )}

      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
